/**
 * It is a RewardsID Entry component for AnonymousUser
 *
 * @module views/components/UltamateRewardsIdLookUp
 * @memberof -Common
 */
import './UltamateRewardsIdLookUp.scss';

import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import Button from '@ulta/core/components/Button/Button';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import useLoader from '@ulta/core/hooks/useLoader/useLoader';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { hasItems } from '@ulta/core/utils/array/array';

import * as utils from './UltamateRewardsIdLookUp';

/**
* Represents a UltamateRewardsIdLookUp component
*
* @method
* @param {UltamateRewardsIdLookUpProps} props - React properties passed from composition
* @returns UltamateRewardsIdLookUp
*/
export const UltamateRewardsIdLookUp = React.forwardRef( ( props, _ )=>{
  const {
    buttonAction,
    closeAction,
    componentKey,
    conditionMessageLabel,
    email,
    helperText,
    findRewardsTitle,
    findRewardsSubTitle,
    findRewardsButtonAction,
    findRewardsHelpText,
    helpImage,
    invokeMutation,
    linkAction,
    loading,
    memberId,
    messages,
    otherWaysToFindLabel,
    phoneNumber,
    showEnterRewardsSection,
    showFindMyIdSection,
    subTitle,
    title,
    id
  } = props;

  const { closeOverlay } = useOverlay();
  const [isButtonClicked, setIsButtonClicked] = useState( false );
  const [isFindMyId, setIsFindMyId] = useState( showFindMyIdSection );
  const [loader, showLoader, hideLoader] = useLoader( {} );
  const showFindMyIdLink = linkAction && helperText && !isFindMyId;
  const showOnlyFindMyIdSection = !showEnterRewardsSection && showFindMyIdSection;
  const containerLayerHostContentId = utils.getContainerLayerHostContentId( props );
  const hasCloseAction = !!closeAction?.label;

  useEffect( () => {
    utils.handleCloseOverlay( { loading, isButtonClicked }, { closeOverlay, hideLoader } );
    utils.handleLoadingState( { loading }, { showLoader, hideLoader } );
    utils.handleFindMyIdSection( { loading, messages, isFindMyId, showOnlyFindMyIdSection }, { setIsFindMyId } );
  }, [loading] );

  return (
    <div className='UltamateRewardsIdLookUp'>
      { loader }
      { showEnterRewardsSection && (
        <AsyncComponent
          buttonAction={ buttonAction }
          componentKey={ componentKey }
          containerLayerHostContentId={ containerLayerHostContentId }
          helperText={ helperText }
          invokeMutation={ invokeMutation }
          loading={ loading }
          moduleName='UltamateRewardsIdLookUpApply'
          memberId={ memberId }
          messages={ messages }
          subTitle={ subTitle }
          title={ title }
        />
      ) }
      { showFindMyIdLink && (
        <div className='UltamateRewardsIdLookUp__LinkAction'>
          <Text htmlTag='p'
            textStyle='body-1'
          >
            { helperText.text }
          </Text>

          { linkAction?.url ? (
            <Link_Huge withHover
              url={ linkAction.url }
              target='_blank'
            >
              { linkAction.label }
            </Link_Huge>
          ) : (
            <Button
              likeLink
              label={ linkAction.label }
              onClick={ () => setIsFindMyId( !isFindMyId ) }
            />
          ) }
        </div>
      ) }
      { isFindMyId && (
        <>
          <AsyncComponent
            buttonAction={ findRewardsButtonAction }
            closeAction={ closeAction }
            componentKey={ componentKey }
            conditionMessageLabel={ conditionMessageLabel }
            containerLayerHostContentId={ containerLayerHostContentId }
            email={ email }
            helperText={ findRewardsHelpText }
            helpImage={ helpImage }
            invokeMutation={ invokeMutation }
            moduleName='UltamateRewardsIdLookUpLocate'
            otherWaysToFindLabel={ otherWaysToFindLabel }
            phoneNumber={ phoneNumber }
            subTitle={ findRewardsSubTitle }
            title={ findRewardsTitle }
            messages={ messages }
            showError={ showOnlyFindMyIdSection }
          />
          { hasCloseAction && (
            <div className='UltamateRewardsIdLookUp__closeAction'>
              <Button
                likeLink
                label={ closeAction.label }
                onClick={ () => setIsFindMyId( !isFindMyId ) }
              />
            </div>
          ) }
        </>
      ) }
    </div>
  );
} );

/**
 * handleCloseOverlay function for updating the state and close the overlay
 * @method
 * @param {object} data - Passing required data as an argument
 * @param {object} methods - Passing setstate methods as an argument
 */
export const handleCloseOverlay = ( data, methods ) => {
  const { hideLoader } = methods;
  const { loading, isButtonClicked } = data;
  if( !loading && isButtonClicked ){
    hideLoader();
  }
};

/**
 * handleLoadingState function to handle loading state
 * @method
 * @param {object} data - Passing loading as an argument
 * @param {object} methods - Passing showLoader, hideLoader methods as an argument
 */
export const handleLoadingState = ( data, methods ) => {
  const { loading } = data;
  const { showLoader, hideLoader } = methods;
  if( loading ){
    showLoader();
  }
  else {
    hideLoader();
  }
};

/**
 * handleFindMyIdSection function to close find my Id section in case of valid mail id
 * @method
 * @param {object} data - Passing required data as an argument
 * @param {object} methods - Passing setIsFindMyId methods as an argument
 */
export const handleFindMyIdSection = ( data, methods ) => {
  const { loading, messages, isFindMyId, showOnlyFindMyIdSection } = data;
  const { setIsFindMyId } = methods;
  const hasMessages = hasItems( messages ) && loading === false && isFindMyId;
  if( hasMessages && !showOnlyFindMyIdSection ){
    setIsFindMyId( !isFindMyId );
  }
};


/**
 * Retrieves the `containerLayerHostContentId` if available; otherwise, returns the `id`.
 *
 * @param {Object} data - The input data object.
 * @param {string} [data.containerLayerHostContentId] - The optional container layer host content ID.
 * @param {string} data.id - The fallback ID if `containerLayerHostContentId` is not provided.
 * @returns {string} - The `containerLayerHostContentId` if it exists, otherwise the `id`.
 */

export const getContainerLayerHostContentId = ( data )=>{
  const { containerLayerHostContentId, id } = data;
  return containerLayerHostContentId || id;
};

/**
* Property type definitions
* @typedef UltamateRewardsIdLookUpProps
* @type {object}
* @property {string} title - Sets the title properties
* @property {string} subTitle - Sets the subTitle properties
* @property {object} linkAction - Sets the linkAction properties
* @property {object} buttonAction - Sets the buttonAction properties
* @property {string} closeAccessibilityLabel - ariaLabel for the close button
* @property {object} closeAction - Sets the closeAction properties
* @property {string} containerLayerHostContentId - sets containerLayerHostContentId
* @property {string} conditionMessageLabel - Sets the conditionMessageLabel
* @property {object} findRewardsButtonAction - Sets the findRewardsButtonAction properties
* @property {string} findRewardsHelpText - sets helperText
* @property {string} findRewardsSubTitle - sets subTitle property
* @property {string} findRewardsTitle - sets title property
* @property {object} phoneNumber - Sets the phoneNumber properties
* @property {object} email - Sets the email properties
* @property {array} helpImage - array of images for findMyIdSection
* @property {string} otherWaysToFindLabel - label property for otherWaysToFind rewards
* @property {boolean} showEnterRewardsSection - sets showEnterRewardsSection
* @property {boolean} showFindMyIdSection - sets showFindMyIdSection
*/
export const propTypes = {
  title: PropTypes.string.isRequired,
  subTitle: PropTypes.string.isRequired,
  memberId: PropTypes.shape( {
    name: PropTypes.string.isRequired
  } ),
  phoneNumber: PropTypes.object,
  email: PropTypes.object,
  buttonAction: PropTypes.shape( {
    label: PropTypes.string.isRequired
  } ),
  linkAction: PropTypes.shape( {
    label: PropTypes.string.isRequired
  } ),
  containerLayerHostContentId: PropTypes.string,
  id: PropTypes.string,
  closeAccessibilityLabel: PropTypes.string,
  messages: PropTypes.array,
  closeAction: PropTypes.object,
  conditionMessageLabel: PropTypes.string,
  findRewardsButtonAction: PropTypes.object,
  findRewardsHelpText: PropTypes.string,
  findRewardsSubTitle: PropTypes.string,
  findRewardsTitle: PropTypes.string,
  helpImage: PropTypes.array,
  otherWaysToFindLabel: PropTypes.string,
  showEnterRewardsSection: PropTypes.bool,
  showFindMyIdSection: PropTypes.bool
};

UltamateRewardsIdLookUp.propTypes = propTypes;
UltamateRewardsIdLookUp.displayName = 'UltamateRewardsIdLookUp';

export default UltamateRewardsIdLookUp;